import { reactive } from 'vue';
import type {
  DriveFileSelection,
  DriveFolder,
  DriveFolderPermission,
  DriveFolderSelection,
  DriveService,
} from '@blpde/connect';
import {
  createDriveFolder as rawCreateFolder,
  downloadDriveFile as rawDownload,
  fetchDriveFolder as rawFetchFolder,
  fetchDriveMounts as rawFetchMounts,
  fetchDriveEntry as rawFetchEntry,
  resolveDriveFolder as rawResolveFolder,
  isDriveCredentialsError as rawIsCreds,
  saveDriveMountCredentials as rawSaveCreds,
  uploadDriveFile as rawUpload,
} from '../api/drive';
import { createDriveAppFiles } from './driveAppFiles';
import { mimeForName, splitDrivePath } from './paths';

const fetchDriveMounts = rawFetchMounts as () => Promise<DriveMount[]>;
const fetchDriveFolder = rawFetchFolder as (args: {
  mountId: number;
  path?: string;
}) => Promise<DriveEntry[]>;
const fetchDriveEntry = rawFetchEntry as (args: {
  mountId: number;
  path: string;
}) => Promise<DriveEntry>;
const resolveDriveFolder = rawResolveFolder as (args: {
  mountId: number;
  documentId: string;
  permission: DriveFolderPermission;
}) => Promise<DriveEntry>;
const downloadDriveFile = rawDownload as (args: {
  mountId: number;
  path: string;
}) => Promise<{
  blob: Blob;
  fileName: string;
  etag?: string;
  documentId?: string;
  revision?: number;
}>;
const uploadDriveFile = rawUpload as (args: {
  file: File;
  mountId: number;
  path?: string;
  ifMatch?: string;
  createOnly?: boolean;
}) => Promise<{ etag?: string; documentId?: string; revision?: number }>;
const createDriveFolder = rawCreateFolder as (args: {
  mountId: number;
  path: string;
}) => Promise<unknown>;
const saveDriveMountCredentials = rawSaveCreds as (args: {
  mountId: number;
  username: string;
  password: string;
  domain?: string;
}) => Promise<unknown>;
export const isDriveCredentialsError = rawIsCreds as (
  error: unknown,
) => boolean;

export interface DriveMount {
  id: number;
  name: string;
  [key: string]: unknown;
}

export interface DriveEntry {
  name: string;
  path: string;
  isDir: boolean;
  mimeType?: string;
  /** Stable identity, unchanged by rename and move. Empty when unavailable. */
  documentId?: string;
  /** Monotonic change counter. 0 when unknown. */
  revision?: number;
  [key: string]: unknown;
}

export type DrivePickerMode = 'open' | 'save' | 'folder';
type DrivePickerSelection = DriveFileSelection | DriveFolderSelection;

export interface DrivePickerState {
  open: boolean;
  mode: DrivePickerMode;
  acceptExtensions: readonly string[];
  defaultName: string;
  /** Where the dialog starts (the request's `startFolder`); null = the last or first mount's root. */
  startFolder: DriveFolder | null;
  mountId?: string | null;
  permission: DriveFolderPermission;
  requestId: number;
  shareItem: DriveFileSelection | null;
  shareDone: (() => void) | null;
}

type PickWaiter = (selection: DrivePickerSelection | null) => void;

export interface DriveFilePickerHandle {
  service: DriveService;
  state: DrivePickerState;
  confirm: (selection: DrivePickerSelection) => void;
  cancel: () => void;
}

function fileFromBytes(bytes: Uint8Array, name: string, type: string): File {
  const copy = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  );
  return new File([copy as ArrayBuffer], name, { type });
}

export function createDriveFilePicker(): DriveFilePickerHandle {
  const state = reactive<DrivePickerState>({
    open: false,
    mode: 'open',
    acceptExtensions: [],
    defaultName: '',
    startFolder: null,
    mountId: null,
    permission: 'read',
    requestId: 0,
    shareItem: null,
    shareDone: null,
  });

  let waiter: PickWaiter | null = null;

  function finish(selection: DrivePickerSelection | null): void {
    const done = waiter;
    waiter = null;
    state.open = false;
    done?.(selection);
  }

  function beginPick(
    mode: DrivePickerMode,
    accept: readonly string[],
    defaultName: string,
    startFolder?: DriveFolder,
    permission: DriveFolderPermission = 'read',
    restrictedMountId?: string,
  ): Promise<DrivePickerSelection | null> {
    if (waiter) finish(null);
    state.mode = mode;
    state.acceptExtensions = accept;
    state.defaultName = defaultName;
    state.startFolder = startFolder ? { ...startFolder } : null;
    state.permission = permission;
    state.mountId = restrictedMountId ?? null;
    state.requestId += 1;
    state.open = true;
    return new Promise((resolve) => {
      waiter = resolve;
    });
  }

  const service: DriveService = {
    async pickFile(options) {
      const selected = await beginPick(
        'open',
        options?.acceptExtensions ?? [],
        '',
        options?.startFolder,
      );
      return selected && 'mimeType' in selected ? selected : null;
    },
    async pickSaveLocation(options) {
      const selected = await beginPick(
        'save',
        options?.acceptExtensions ?? [],
        options?.defaultName ?? '',
        options?.startFolder,
        'write',
      );
      return selected && 'mimeType' in selected ? selected : null;
    },
    async pickFolder(options) {
      const selected = await beginPick(
        'folder',
        [],
        '',
        options?.startFolder,
        options?.permission,
        options?.mountId,
      );
      return selected && !('mimeType' in selected) ? selected : null;
    },
    async resolveFolder(reference, permission = 'read') {
      const mounts = await fetchDriveMounts();
      const mount = mounts.find(
        (item) => String(item.id) === reference.mountId,
      );
      if (!mount) throw new Error('Drive mount is unavailable');
      const entry = await resolveDriveFolder({
        mountId: mount.id,
        documentId: reference.documentId,
        permission,
      });
      if (!entry.isDir || entry.documentId !== reference.documentId) {
        throw new Error('Drive folder is unavailable');
      }
      return {
        mountId: String(mount.id),
        ...(typeof entry.companyId === 'string'
          ? { companyId: entry.companyId }
          : {}),
        mountName: mount.name,
        documentId: entry.documentId,
        path: entry.path,
        name: entry.name,
      };
    },
    async download(selection) {
      const result = await downloadDriveFile({
        mountId: Number(selection.mountId),
        path: selection.path,
      });
      const bytes = new Uint8Array(await result.blob.arrayBuffer());
      selection.etag = result.etag || undefined;
      // Refreshed on every transfer, not only when the selection is first
      // made: a file renamed since the picker ran keeps its identity, and
      // this is where the caller learns the current revision of what it just
      // read.
      if (result.documentId) selection.documentId = result.documentId;
      if (result.revision) selection.revision = result.revision;
      return bytes;
    },
    async upload(selection, bytes, mimeType, condition) {
      const { dir, name } = splitDrivePath(selection.path);
      const file = fileFromBytes(
        bytes,
        name || selection.name,
        mimeType || mimeForName(name || selection.name),
      );
      const result = await uploadDriveFile({
        file,
        mountId: Number(selection.mountId),
        // Without the slash Drive reads the folder as the file's own name.
        path: dir ? `${dir}/` : '',
        ...(condition?.etag ? { ifMatch: condition.etag } : {}),
        ...(condition?.createOnly ? { createOnly: true } : {}),
      });
      selection.etag = result?.etag || undefined;
      if (result?.documentId) selection.documentId = result.documentId;
      if (result?.revision) selection.revision = result.revision;
    },
    share(selection) {
      if (state.shareDone) state.shareDone();
      state.shareItem = selection;
      return new Promise((resolve) => {
        state.shareDone = () => {
          state.shareItem = null;
          state.shareDone = null;
          resolve();
        };
      });
    },
    appFiles(options) {
      return createDriveAppFiles(options, {
        download: (selection) => service.download(selection),
        upload: (selection, bytes, mimeType, condition) => service.upload(selection, bytes, mimeType, condition),
      });
    },
  };

  return {
    service,
    state,
    confirm: (selection) => {
      if (state.mountId && selection.mountId !== state.mountId) return;
      finish(selection);
    },
    cancel: () => finish(null),
  };
}

export async function listDriveMounts(): Promise<DriveMount[]> {
  return fetchDriveMounts();
}

export async function listDriveFolder(
  mountId: number,
  path: string,
): Promise<DriveEntry[]> {
  return fetchDriveFolder({ mountId, path });
}

export async function statDriveFolder(
  mountId: number,
  path: string,
): Promise<DriveEntry & { documentId: string }> {
  const entry = await fetchDriveEntry({ mountId, path });
  if (!entry.isDir || !entry.documentId)
    throw new Error('Drive folder identity is unavailable');
  return { ...entry, documentId: entry.documentId };
}

export async function mkdirDrive(mountId: number, path: string): Promise<void> {
  await createDriveFolder({ mountId, path });
}

export async function saveMountCredentials(args: {
  mountId: number;
  username: string;
  password: string;
  domain?: string;
}): Promise<void> {
  await saveDriveMountCredentials(args);
}
